import {Color} from '@benchling/nucleus-ui';
import * as d3 from 'd3';

import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import Type from 'molbio/maps/Type';
import {AminoAcid} from 'molbio/MolBioConstants';
import * as sequtil from 'molbio/SeqUtil';
import Translation from 'molbio/translations/backbone/Translation';
import {
  DEFAULT_TRANSLATION_COLOR,
  getTranslationRegionFormattedName,
} from 'molbio/translations/TranslationUtil';
import {ellipsize} from 'util/NameUtil';

export interface DisplayAminoAcid extends AminoAcid {
  bp: number;
  startPos: number;
  endPos: number;
}

export function getDisplayAminoAcids(
  aas: Array<AminoAcid>,
  sliceStart: number,
  sliceEnd: number,
  sequenceLength: number
): Array<DisplayAminoAcid> {
  return aas.map((aa: DisplayAminoAcid) => {
    const startPos = aa.indexStart > sliceEnd ? sliceStart : Math.max(aa.indexStart, sliceStart);
    const endPos = aa.indexEnd < sliceStart ? sliceEnd : Math.min(sliceEnd, aa.indexEnd);
    const bp = sequtil.length(startPos, endPos, sequenceLength) % sequenceLength;
    return {...aa, startPos, endPos, bp};
  });
}

/**
 * Get the height in pixels of a single translation.
 * @param textHeight Height of the residue and name font
 * @param nameShown Whether the name label is shown
 * @param indexTypeHeight Height of amino acid index font
 * @param indicesShown Whether AA indices are visible
 */
export function getSingleTranslationHeight(
  textHeight: number,
  nameShown: boolean,
  indexTypeHeight: number,
  indicesShown: boolean
): number {
  const indexHeight = indicesShown ? indexTypeHeight : 0;
  return (nameShown ? textHeight * 2 : textHeight) + indexHeight;
}

/**
 * Gets the color and font properties for rendering an amino acid as part of a translation on the linear map.
 */
export function getTranslationAADisplayProperties(
  aa: AminoAcid,
  showSelectedState: boolean,
  opacityFactor: number
): {aaFillColor: string; aaGlyphColor: string; fontWeight: number; fillOpacity: number} {
  const aaFillColor =
    BenchlingConfig.MOLBIO_AA_SEQUENCE_MAP_REFRESH && showSelectedState
      ? // d3.darker has a 'fun' quirk where the argument to darker is not a flat % by which each rgb value
        // will get multiplied, but instead an exponent which d3's hardcoded 'darker' value of 0.7 will be
        // raised to the power of. 0.45 makes the colors about 15% darker (0.7 ^ 0.45 ~= .8517).
        d3.rgb(aa.backgroundColor).darker(0.45).formatHex()
      : aa.backgroundColor;
  const aaGlyphColor = BenchlingConfig.MOLBIO_AA_SEQUENCE_MAP_REFRESH ? aa.glyphColor : Color.BLACK;
  const fontWeight = showSelectedState && BenchlingConfig.MOLBIO_AA_SEQUENCE_MAP_REFRESH ? 600 : 400;

  let fillOpacity = 1;
  if (showSelectedState) {
    fillOpacity = BenchlingConfig.MOLBIO_AA_SEQUENCE_MAP_REFRESH ? 1 : 0.75;
  } else {
    fillOpacity = (BenchlingConfig.MOLBIO_AA_SEQUENCE_MAP_REFRESH ? 1 : 0.5) * opacityFactor;
  }

  return {aaFillColor, aaGlyphColor, fontWeight, fillOpacity};
}

/**
 * Returns the ellipsized translation name if the name is longer than the available character space.
 */
function formatTranslationDisplayName(name: string, nAvailableChars: number): string {
  if (nAvailableChars < name.length) {
    return nAvailableChars < 5 ? '' : ellipsize(name, nAvailableChars);
  }
  return name;
}

const TRANSLATION_NAME_LINE_HEIGHT = 4;
const TRANSLATION_NAME_PADDING = 4;
/**
 * Calcualtes attributes for rendering name label for a translation on a linear sequence map slice.
 * @param sequence    Sequence model that the translation belongs to
 * @param translation Translation model rendered
 * @param fontType    Font Type for translation slice text
 * @param indexHeight Pixel height taken up by AA indices. 0 if not shown.
 * @param width       Width in pixels that the translation takes up on the slice
 * @param xTranslate  X offset in pixels where the translation starts on the slice
 */
export function getTranslationNameLabelProperties({
  sequence,
  translation,
  fontType,
  indexHeight,
  width,
  xTranslate,
}: {
  sequence: Sequence;
  translation: Translation;
  fontType: Type;
  indexHeight: number;
  width: number;
  xTranslate: number;
}): {
  lineWidth: number;
  lineHeight: number;
  lineX: number;
  lineY: number;
  fill: string;
  nameBoxWidth: number;
  nameBoxHeight: number;
  nameBoxX: number;
  nameBoxY: number;
  displayName: string;
  fullTooltipName: string | null;
  nameFontSize: number;
  nameTextX: number;
  nameTextY: number;
} {
  const group = translation.getTranslationRegionGroup(sequence);
  const fontHeight = fontType.height();
  const translationName =
    group == null
      ? ''
      : getTranslationRegionFormattedName(group, translation.id, sequence.startIndex(), sequence.length());
  const displayNameWidth = width - TRANSLATION_NAME_PADDING * 2;
  const nAvailableChars = displayNameWidth / fontType.charWidth();
  // Truncated name to display that fits the allotted space
  const displayName = formatTranslationDisplayName(translationName, nAvailableChars);
  // Y position of the name starts below the space taken by AA indices and arrows (font height)
  const nameY = indexHeight + fontHeight;
  // If there is a name to show, the box width should fit all characters and padding on both sides
  const nameBoxWidth =
    displayName.length > 0 ? displayName.length * fontType.charWidth() + TRANSLATION_NAME_PADDING * 2 : 0;
  // If the full display name was truncated, show the full name in a tooltip
  const fullTooltipName = nAvailableChars < translationName.length ? translationName : null;

  return {
    lineWidth: width,
    lineHeight: TRANSLATION_NAME_LINE_HEIGHT,
    lineX: xTranslate,
    lineY: nameY + (fontHeight / 2 - TRANSLATION_NAME_LINE_HEIGHT / 2),
    fill: group?.color ?? DEFAULT_TRANSLATION_COLOR,
    nameBoxWidth,
    nameBoxHeight: fontHeight,
    nameBoxX: xTranslate,
    nameBoxY: nameY,
    displayName,
    fullTooltipName,
    nameFontSize: fontType.fontSize - 2,
    nameTextX: xTranslate + TRANSLATION_NAME_PADDING,
    nameTextY: nameY + fontType.fontSize - TRANSLATION_NAME_PADDING / 2,
  };
}
